'use client';

import { Fragment } from 'react';
import { useLocale, useTranslations } from 'next-intl';

type Box = { num: string; title: string; sub: string };
type Cycle = { num: string; label: string; steps: string[]; center: string; exit: string };

const DARK = 'var(--font-secondary)';
const NAVY = 'var(--font-navy)';

/**
 * "Der Ablauf" — three independent doors, not one staircase. Each of the three
 * modules (knowledge workshop, analysis workshop, implementation) is its own
 * clickable entry point with its own small "enter here" arrow; the connectors
 * between them are only faintly hinted, so the picture says "start anywhere".
 */
function FlowBox({ box }: { box: Box }) {
  return (
    <div className="flex w-full max-w-[260px] lg:max-w-none lg:w-[180px] shrink-0 flex-col gap-1.5 rounded-2xl bg-white p-6 shadow-[0_6px_22px_-12px_rgba(16,24,40,0.18)] transition-shadow duration-300 group-hover:shadow-[0_14px_34px_-14px_rgba(16,24,40,0.30)]">
      <span className="font-mono text-eyebrow font-semibold text-font-navy">{box.num}</span>
      <span className="text-subheadline font-medium leading-tight text-font-secondary">{box.title}</span>
      <span className="text-nav text-font-tertiary">{box.sub}</span>
    </div>
  );
}

/** Small "enter here" arrow that sits above each door. */
function EntryArrow() {
  return (
    <svg width="16" height="22" viewBox="0 0 16 22" className="text-font-navy/45 transition-colors duration-300 group-hover:text-font-navy" aria-hidden>
      <line x1="8" y1="1" x2="8" y2="14" stroke="currentColor" strokeWidth="1.5" />
      <polyline points="3,10 8,17 13,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Faint, dashed connector between doors — only hinted, never a dominant chain. */
function FaintConnector() {
  return (
    <div className="hidden lg:flex shrink-0 self-center text-font-secondary/20" aria-hidden>
      <svg width="30" height="8" viewBox="0 0 30 8">
        <line x1="0" y1="4" x2="23" y2="4" stroke="currentColor" strokeWidth="1" strokeDasharray="2 3" />
        <polyline points="20,1.5 26,4 20,6.5" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export function CycleVisual({ cycle }: { cycle: Cycle }) {
  const C = { x: 125, y: 112 };
  const R = 50;
  // Three nodes 120° apart: top, bottom-right, bottom-left.
  const at = (deg: number) => ({
    x: C.x + R * Math.cos((deg * Math.PI) / 180),
    y: C.y + R * Math.sin((deg * Math.PI) / 180),
  });
  const n = [at(-90), at(30), at(150)];
  const arc = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} A ${R} ${R} 0 0 1 ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;
  const arcs = [arc(n[0], n[1]), arc(n[1], n[2]), arc(n[2], n[0])];

  return (
    <svg viewBox="0 0 250 200" className="w-[210px] shrink-0" style={{ overflow: 'visible' }} aria-hidden>
      <defs>
        <marker id="cyc-ah" markerWidth="8" markerHeight="8" refX="5" refY="3" orient="auto" markerUnits="userSpaceOnUse">
          <path d="M0,0 L6,3 L0,6 Z" style={{ fill: DARK }} />
        </marker>
      </defs>

      <text x="125" y="14" textAnchor="middle" className="font-mono uppercase" style={{ fill: NAVY }} fontSize="11" fontWeight="600" letterSpacing="2">
        {cycle.num ? `${cycle.num} · ${cycle.label}` : cycle.label}
      </text>

      <circle cx={C.x} cy={C.y} r={R} fill="none" style={{ stroke: NAVY }} strokeWidth="1" opacity="0.25" />
      {arcs.map((d, i) => (
        <path key={i} d={d} fill="none" style={{ stroke: DARK }} strokeWidth="2" markerEnd="url(#cyc-ah)" />
      ))}
      {n.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" style={{ fill: DARK }} />
      ))}

      <text x="125" y="50" textAnchor="middle" style={{ fill: DARK }} fontSize="12" fontWeight="700">{cycle.steps[0]}</text>
      <text x={n[1].x + 10} y={n[1].y + 5} textAnchor="start" style={{ fill: DARK }} fontSize="12" fontWeight="700">{cycle.steps[1]}</text>
      <text x={n[2].x - 10} y={n[2].y + 5} textAnchor="end" style={{ fill: DARK }} fontSize="12" fontWeight="700">{cycle.steps[2]}</text>

      <text x="125" y="116" textAnchor="middle" style={{ fill: NAVY }} fontSize="10" fontWeight="600">{cycle.center}</text>
    </svg>
  );
}

export function ProcessFlow() {
  const t = useTranslations('dataTranslation');
  const locale = useLocale();
  const box1 = t.raw('flow_box1') as Box;
  const box2 = t.raw('flow_box2') as Box;
  const cycle = t.raw('flow_cycle') as Cycle;
  const base = `/${locale}/leistungen`;

  const doors = [
    { href: `${base}/ki-wissen-workshop`, node: <FlowBox box={box1} /> },
    { href: `${base}/analyse-workshop`, node: <FlowBox box={box2} /> },
    {
      href: `${base}/individualsoftware`,
      node: (
        <div className="flex flex-col items-center gap-3 rounded-2xl bg-white px-4 pt-4 pb-5 shadow-[0_6px_22px_-12px_rgba(16,24,40,0.18)] transition-shadow duration-300 group-hover:shadow-[0_14px_34px_-14px_rgba(16,24,40,0.30)]">
          <CycleVisual cycle={cycle} />
          <span className="text-[length:var(--text-tag)] italic text-font-tertiary">{cycle.exit}</span>
          <span className="flex items-center justify-center rounded-full bg-font-secondary px-7 py-3 font-display text-button font-medium text-font-primary">
            {t('flow_golive')}
          </span>
        </div>
      ),
    },
  ];

  return (
    <section className="cv-auto bg-background-primary py-24 px-[10%]">
      <div className="max-w-6xl flex flex-col gap-10">

        <div className="flex flex-col gap-3 max-w-3xl">
          <p className="font-mono text-eyebrow font-semibold tracking-[var(--hero-label-tracking)] uppercase text-font-navy">
            {t('block2_eyebrow')}
          </p>
          <h2 className="text-[length:var(--text-section-title)] font-normal tracking-[-0.02em] leading-[1.1] text-font-secondary">
            {t('block2_title')}
          </h2>
          <p className="text-subheadline text-font-tertiary/80 font-light leading-relaxed">
            {t('block2_body')}
          </p>
        </div>

        {/* Drei Türen: jeder Block eigenständig anklickbar, eigener Einstiegspfeil,
            Verbindungen nur leicht angedeutet. */}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col lg:flex-row items-center lg:items-start lg:justify-between gap-6 lg:gap-2">
            {doors.map((d, i) => (
              <Fragment key={d.href}>
                <a href={d.href} className="group flex w-full lg:w-auto lg:flex-1 flex-col items-center gap-2.5">
                  <EntryArrow />
                  <div className="flex w-full justify-center transition-transform duration-300 group-hover:-translate-y-1">
                    {d.node}
                  </div>
                </a>
                {i < doors.length - 1 && <FaintConnector />}
              </Fragment>
            ))}
          </div>
          <p className="text-nav italic text-font-tertiary/70 text-center">
            {t('flow_hint')}
          </p>
        </div>

      </div>
    </section>
  );
}
